import React, { useEffect, useState } from "react";
import { Modal, Form, Select, Input, Button, message, Spin, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { launchPositWorkbenchFlow, getSessionOrComputeEnvs } from "components/LaunchPositWorkbenchModal/launchPositWorkbenchApi";
import "./style.scss";
import { gxpAuditReasons as gxpAccessReasons } from "const";

interface Props {
  visible: boolean;
  onCancel: () => void;
  msalInstance: any;
  region?: string;
  bucketName?: string;
  dataAccountId?: string;
  spaceDisplayName?: string;
  gxpFlag?: string;
  onLaunched?: (url: string) => void;
}

const ValidationRules = {
  requiredField: {
    required: true,
    message: "This field is required.",
  },
};

const LaunchPositWorkbenchModal: React.FC<Props> = ({
  visible,
  onCancel,
  msalInstance,
  region,
  bucketName,
  dataAccountId,
  spaceDisplayName,
  gxpFlag,
  onLaunched,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [computeEnvs, setComputeEnvs] = useState<any>(null);
  const [activeSessionUrl, setActiveSessionUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isGxpSpace = gxpFlag?.toLowerCase() === "gxp";

  const resourceProfiles = computeEnvs?.clusters?.[0]?.resourceProfiles || [];

  const fetchSessionAndComputeEnvs = async () => {
    setFetching(true);
    setError(null);
    try {
      const result = await getSessionOrComputeEnvs(msalInstance, spaceDisplayName || "");

      if (result.hasActiveSession && result.url) {
        setActiveSessionUrl(result.url);
        setComputeEnvs(null);
        message.info("You have an active Posit Workbench session");
      } else {
        setActiveSessionUrl(null);
        setComputeEnvs(result.computeEnvs);
      }
    } catch (err: any) {
      const raw = err?.message || String(err) || "";
      let msg = "Failed to load Posit Workbench data";

      // Map known unauthorized/get_compute_envs errors to a friendly message
      if (/401|unauthorized|unauthorized for url|get_compute_envs/i.test(raw)) {
        msg = "You do not have access to Posit Workbench.";
        // Ensure we don't show the form
        setComputeEnvs(null);
      } else {
        msg = raw || msg;
      }

      setError(msg);
      console.error("Error fetching session or compute envs:", err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (visible && spaceDisplayName) {
      fetchSessionAndComputeEnvs();
    }
  }, [visible]);

  const handleFinish = async (values: any) => {
    if (!region || !bucketName || !dataAccountId) {
      message.error("Missing space configuration data");
      return;
    }

    setLoading(true);
    try {
      // Build reason payload for GxP spaces: map selected value to label,
      // and if an "Other (specify)" value is chosen, append the user-provided text.
      let reasonToSend = values.reason;
      if (isGxpSpace) {
        const sel = values.reason;
        let label = sel;
        for (const group of gxpAccessReasons) {
          const opt = group.options.find((o: any) => o.value === sel);
          if (opt) {
            label = opt.label;
            break;
          }
        }

        if (sel && typeof sel === "string" && sel.endsWith("Other-specify")) {
          const other = (values.reasonOther || "").trim();
          reasonToSend = other ? `${label}: ${other}` : label;
        } else {
          reasonToSend = label;
        }
      }

      const url = await launchPositWorkbenchFlow(
        msalInstance,
        values.resourceProfile,
        values.accessScope,
        region,
        bucketName,
        dataAccountId,
        spaceDisplayName || "",
        reasonToSend,
        resourceProfiles,
        computeEnvs,
      );

      if (url && typeof url === "string" && url.startsWith("http")) {
        // Open the launched session in a new tab and notify parent
        try {
          window.open(url, "_blank", "noopener,noreferrer");
        } catch (err) {
          // ignore
        }
        message.success("Posit Workbench session launched successfully");
        onLaunched?.(url);
      } else {
        throw new Error("Invalid URL received from API");
      }
    } catch (err: any) {
      console.error("Launch Posit Workbench error:", err);
      message.error(err?.message || "Failed to launch Posit Workbench session");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveSessionUrl(null);
    setComputeEnvs(null);
    setError(null);
    onCancel();
  };

  return (
    <Modal title="Launch Posit Workbench Session" open={visible} onCancel={handleCancel} footer={null}>
      <Spin spinning={fetching}>
        {!fetching && error ? (
          <div style={{ color: "#ff4d4f", marginBottom: "16px" }}>
            {error}
          </div>
        ) : activeSessionUrl ? (
          <div style={{ marginBottom: "16px" }}>
            <Button type="primary" onClick={() => window.open(activeSessionUrl, "_blank", "noopener,noreferrer")} style={{ width: "100%" }}>
              Open Active Session
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Form.Item label="Resource profile" name="resourceProfile" rules={[ValidationRules.requiredField]} required>
              <Select placeholder="Select resource profile" options={resourceProfiles.map((profile: any) => ({ label: `${profile.displayName || profile.name}`, value: profile.name }))} />
            </Form.Item>

            <Form.Item label="Access scope" name="accessScope" rules={[ValidationRules.requiredField]} required>
              <Select placeholder="Select Access Scope" options={[{ label: "S3 Access", value: "s3" }, { label: "Full Project Access", value: "full_access" }]} />
            </Form.Item>

            {isGxpSpace && (
              <>
                <Form.Item
                  label={
                    <span>
                      Reason
                      <Tooltip title="Mandatory to provide valid reason to access GxP bucket for audit and compliance tracking.">
                        <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 8 }} />
                      </Tooltip>
                    </span>
                  }
                  name="reason"
                  rules={[ValidationRules.requiredField]}
                  required
                >
                  <Select placeholder="Select reason">
                    {gxpAccessReasons.map((group) => (
                      <Select.OptGroup key={group.group} label={group.group}>
                        {group.options.map((opt: any) => (
                          <Select.Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item shouldUpdate={(prev, curr) => prev.reason !== curr.reason} noStyle>
                  {({ getFieldValue }) => {
                    const sel = getFieldValue("reason") || "";
                    const showOther = typeof sel === "string" && sel.endsWith("Other-specify");
                    return showOther ? (
                      <Form.Item name="reasonOther" rules={[{ required: true, message: "Please specify the reason." }, { max: 200, message: "Reason cannot exceed 200 characters." }]}>
                        <Input.TextArea rows={4} placeholder="Specify reason (min 10 characters)" minLength={10} maxLength={200} showCount />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" loading={loading} htmlType="submit">
                Launch
              </Button>
            </div>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};

export default LaunchPositWorkbenchModal;
